import { cn } from '@quoter/web-core'
import { SCORES, type Score } from '@/lib/plan'
import { useI18n } from '@/hooks/useI18n'

// The same 0–4 scale as the food table's colours, by design: 0–1 green,
// 2 amber, 3–4 red. Each option is a labelled button, never colour alone.
const ACTIVE: Record<Score, string> = {
  0: 'border-[#3d9950] bg-[#dcefd9] text-[#1f5a2a]',
  1: 'border-[#7fb56a] bg-[#e8f3d6] text-[#35591a]',
  2: 'border-[#d99a14] bg-[#fbe7b8] text-[#7a4d00]',
  3: 'border-[#e0763a] bg-[#fbd9c3] text-[#8a3a0c]',
  4: 'border-[#d0492f] bg-[#f8d3cb] text-[#8c2415]',
}
export const SCORE_DOT: Record<Score, string> = {
  0: 'bg-[#3d9950]',
  1: 'bg-[#7fb56a]',
  2: 'bg-[#d99a14]',
  3: 'bg-[#e0763a]',
  4: 'bg-[#d0492f]',
}

export function scoreLabel(t: { scores: Record<string, string> }, score: Score): string {
  return t.scores[`s${score}`] ?? String(score)
}

/** Five buttons, 0 (fine) to 4 (severe). `value` null means nothing chosen yet. */
export function ScorePicker({
  value,
  onChange,
  disabled,
  size = 'md',
}: {
  value: Score | null
  onChange: (score: Score) => void
  disabled?: boolean
  size?: 'sm' | 'md'
}) {
  const { t } = useI18n()
  return (
    <div role="radiogroup" aria-label={t.scores.label} className="flex flex-wrap gap-2">
      {SCORES.map((score) => {
        const active = value === score
        return (
          <button
            key={score}
            type="button"
            role="radio"
            aria-checked={active}
            disabled={disabled}
            onClick={() => onChange(score)}
            className={cn(
              'rounded-full border font-semibold transition-colors disabled:opacity-50',
              size === 'sm' ? 'px-3 py-1 text-[12.5px]' : 'px-4 py-2 text-[13.5px]',
              active ? ACTIVE[score] : 'border-DEFAULT text-gray-700 hover:bg-gray-900/[0.06]',
            )}
          >
            {scoreLabel(t, score)}
          </button>
        )
      })}
    </div>
  )
}
